<template>
  <div class="condition-group">
    <div class="condition-group-rules">
      <div class="condition-group-rules__heading">
        <span class="condition-group-rules__heading-text">
          <template v-if="group.rules.length === 1">When the following statement is true</template>
          <template v-else if="group.rules.length > 1">
            When {{ group.logic.binding == 'AND' ? 'ALL' : 'ANY' }} of the following are true
          </template>
        </span>
        <BaseIcon
          v-if="groupIndex > 0"
          name="minus"
          @click="$emit('removeGroup', groupIndex)"
          size="16"
        />
      </div>
      <div v-for="(rule, ruleIndex) in group.rules" :key="ruleIndex" class="condition-group-rule">
        <div class="condition-group-rule__before">
          <span v-if="ruleIndex === 0">Where</span>
          <SelectInput
            v-else-if="ruleIndex === 1"
            id="group-logic"
            :options="[
              { label: 'And', value: 'AND' },
              { label: 'Or', value: 'OR' },
            ]"
            v-model="group.logic.binding"
            class="p-0 logic-selector"
          />
          <span v-else>
            {{
              group.logic.binding.charAt(0).toUpperCase() +
              group.logic.binding.slice(1).toLowerCase()
            }}
          </span>
        </div>
        <div class="condition-group-rule__body">
          <SelectInput
            id="rule-element"
            :options="
              items.map((item) => ({
                label: item.label,
                value: item.id,
              }))
            "
            v-model="rule.elementId.binding"
            placeholder="Select field"
            :error="rule.elementId.error"
          />
          <SelectInput
            id="rule-operation"
            :options="getAvailableOperations(rule.elementId.binding)"
            v-model="rule.operation.binding"
            :error="rule.operation.error"
          />

          <template
            v-if="isValueInputRequired(rule.operation.binding) && (
              getSelectedElementType(rule.elementId.binding) === 'single-choice' ||
              getSelectedElementType(rule.elementId.binding) === 'multiple-choice' ||
              getSelectedElementType(rule.elementId.binding) === 'picture-choice'
            )"
          >
            <SelectInput
              v-model="rule.value.binding"
              :options="getElementChoices(rule.elementId.binding)"
              placeholder="Select value"
              class="condition-group-rule__input"
              :error="rule.value.error"
            />
          </template>
          <template
            v-else-if="isValueInputRequired(rule.operation.binding) && (
              getSelectedElementType(rule.elementId.binding) === 'number' ||
              getSelectedElementType(rule.elementId.binding) === 'slider'
            )"
          >
            <NumberInput
              v-model="rule.value.binding"
              placeholder="Enter number"
              class="condition-group-rule__input"
              :error="rule.value.error"
            />
          </template>
          <template
            v-else-if="
              isValueInputRequired(rule.operation.binding) &&
              getSelectedElementType(rule.elementId.binding) === 'switch'
            "
          >
            <SelectInput
              v-model="rule.value.binding"
              :options="[
                { label: 'true', value: 'true' },
                { label: 'false', value: 'false' },
              ]"
              placeholder="Select value"
              class="condition-group-rule__input"
              :error="rule.value.error"
            />
          </template>
          <template v-else-if="isValueInputRequired(rule.operation.binding)">
            <TextInput
              v-model="rule.value.binding"
              placeholder="Enter value"
              class="condition-group-rule__input"
              :error="rule.value.error"
            />
          </template>
        </div>
        <div class="condition-group-rule__after">
          <BaseIcon
            v-if="ruleIndex > 0"
            name="minus"
            @click="$emit('removeRule', groupIndex, ruleIndex)"
            size="16"
          />
        </div>
      </div>
      <div class="condition-group-rules__actions">
        <Btn @click.prevent="$emit('addRule', groupIndex)" icon="plus-circle" variant="tertiary">
          Add filter
        </Btn>
        <!-- <Btn
          @click="$emit('addGroup', groupIndex)"
          icon="plus-circle"
          text="Add sub-group"
          variant="outlined"
        /> -->
      </div>
      <div v-if="group.subGroups">
        <Group
          v-for="(subGroup, subGroupIndex) in group.subGroups"
          :key="subGroupIndex"
          :group="subGroup"
          :groupIndex="subGroupIndex"
          @addRule="addRule"
          @removeRule="removeRule"
          @addGroup="addGroup"
          @removeGroup="removeGroup"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { computed } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import { useFormStore } from '@/store/formStore';

  import { OPERATIONS_BY_TYPE } from '@forms/utils/conditionOperations';

  export default {
    name: 'Group',
    components: {
      BaseIcon,
      Btn,
      SelectInput,
      TextInput,
      NumberInput,
    },
    props: {
      group: Object,
      groupIndex: Number,
      items: Array,
    },
    setup(props) {
      const getElementChoices = (elementId) => {
        const selectedItem = props.items.find((item) => item.id === elementId);
        return (
          selectedItem?.choices?.map((choice) => ({
            label: choice.content,
            value: choice.id,
          })) || []
        );
      };

      const getSelectedElementType = (elementId) => {
        const selectedItem = props.items.find((item) => item.id === elementId);
        return selectedItem?.type || '';
      };

      const getAvailableOperations = (elementId) => {
        const elementType = getSelectedElementType(elementId);
        return OPERATIONS_BY_TYPE[elementType] || OPERATIONS_BY_TYPE.default;
      };

      const isValueInputRequired = (operation) => {
        return !['is_empty', 'is_not_empty'].includes(operation);
      };

      return {
        getSelectedElementType,
        getElementChoices,
        getSelectedElementType,
        getAvailableOperations,
        isValueInputRequired,
      };
    },
  };
</script>
<style lang="scss" scoped>
  .condition-group {
    display: flex;
    background-color: var(--slate-50);
    border-radius: 0.5rem;
    border: 1px solid var(--slate-200);
    padding: 0.5rem;
    .condition-group-rules {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      gap: 0.5rem;
      &__heading {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        color: var(--slate-500);
        .icon {
          color: var(--slate-400);
          @include hover {
            color: var(--slate-700);
          }
        }
      }
      .condition-group-rule {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        gap: 1rem;
        &__before {
          width: 5rem;
          color: var(--slate-500);
        }
        &__body {
          display: flex;
          flex-grow: 1;
          gap: 0.25rem;
          .form-group {
            padding: 0;
          }
        }
        &__after {
          .icon {
            color: var(--slate-400);
            @include hover {
              color: var(--slate-700);
            }
          }
        }
        :deep(.logic-selector) {
          select {
            padding: 0.25rem 0.75rem 0.25rem 0;
            background-color: transparent !important;
            color: var(--slate-800);
            font-weight: 500;
            border-color: transparent;
          }
        }
      }
      &__actions {
        margin-top: 1rem;
      }
    }
  }
</style>
